// @flow
import {ofType} from 'redux-observable';
import {catchError, map, switchMap} from 'rxjs/operators';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {
  getCurrencyApi,
  getCurrenciesApi,
  setCurrencyApi,
  getGeolocationApi,
} from './api';
import {LoginSuccess, GetSessionSuccess} from '../auth/duck';

import type {Action} from '../../store/utils';
import type {ActionObservable} from 'redux-observable';
import type {Country, Currency} from '../../entities';
import type {ApiError} from '../../http/error';
import {of} from 'rxjs';
import {captureMessage} from '@sentry/react';

export type CurrencyAction = Action & {
  currencies?: Currency[],
  currencyId?: string,
  country?: Country,
  error?: string,
};

export type CurrencyState = {
  isFetchingCurrency?: boolean,
  isFetching?: boolean,
  isSaving?: boolean,
  currency?: Currency,
  currencies?: Currency[],
  isFetchingCurrencies?: boolean,
  fetchCurrencySuccess?: boolean,
  fetchCurrencyError?: string,
  setCurrencySuccess?: boolean,
  setCurrencyError?: string,
  country?: Country,
  getGeolocationError?: string,
};

export class FetchCurrency extends ActionCreator<CurrencyAction> {}
export class FetchCurrencySuccess extends ActionCreator<CurrencyAction> {}
export class FetchCurrencyError extends ActionCreator<CurrencyAction> {}
export class FetchCurrencies extends ActionCreator<CurrencyAction> {}
export class FetchCurrenciesSuccess extends ActionCreator<CurrencyAction> {}
export class FetchCurrenciesError extends ActionCreator<CurrencyAction> {}
export class SetCurrency extends ActionCreator<CurrencyAction> {}
export class SetCurrencySuccess extends ActionCreator<CurrencyAction> {}
export class SetCurrencyError extends ActionCreator<CurrencyAction> {}
export class FetchGeolocation extends ActionCreator<CurrencyAction> {}
export class FetchGeolocationSuccess extends ActionCreator<CurrencyAction> {}
export class FetchGeolocationError extends ActionCreator<CurrencyAction> {}

export const actions = {
  fetchCurrency: (payload: CurrencyAction) => new FetchCurrency(),
  fetchCurrencies: (payload: CurrencyAction) => new FetchCurrencies(),
  setCurrency: (payload: CurrencyAction) => new SetCurrency(payload),
};

export const initialState = {
  isFetching: true,
};

export default createReducer(initialState, {
  [FetchCurrency.type]: (state: CurrencyState) => ({
    ...state,
    isFetchingCurrency: true,
  }),
  [LoginSuccess.type]: (state: CurrencyState) => ({
    ...state,
    isFetchingCurrency: true,
  }),
  [GetSessionSuccess.type]: (state: CurrencyState) => ({
    ...state,
    isFetchingCurrency: true,
  }),
  [FetchCurrencySuccess.type]: (
    state: CurrencyState,
    action: CurrencyAction
  ) => ({
    ...state,
    isFetchingCurrency: undefined,
    currency: action.currency,
  }),
  [FetchCurrencyError.type]: (
    state: CurrencyState,
    action: CurrencyAction
  ) => ({
    ...state,
    isFetchingCurrency: undefined,
    fetchCurrencyError: action.error,
  }),
  [FetchCurrencies.type]: (state: CurrencyState) => ({
    ...state,
    isFetching: true,
  }),
  [FetchCurrenciesSuccess.type]: (
    state: CurrencyState,
    action: CurrencyAction
  ) => ({
    ...state,
    isFetching: undefined,
    currencies: action.currencies,
  }),
  [FetchCurrenciesError.type]: (
    state: CurrencyState,
    action: CurrencyAction
  ) => ({
    ...state,
    isFetching: undefined,
    fetchCurrenciesError: action.error,
  }),
  [SetCurrency.type]: (state: CurrencyState) => ({
    ...state,
    isSaving: true,
    setCurrencySuccess: undefined,
  }),
  [SetCurrencySuccess.type]: (state: CurrencyState) => ({
    ...state,
    isSaving: undefined,
    setCurrencySuccess: true,
  }),
  [SetCurrencyError.type]: (state: CurrencyState, action: CurrencyAction) => ({
    ...state,
    isSaving: undefined,
    setCurrencyError: action.error,
  }),
  [FetchGeolocation.type]: (state: CurrencyState) => ({
    ...state,
    // isFetching: true,
  }),
  [FetchGeolocationSuccess.type]: (
    state: CurrencyState,
    action: CurrencyAction
  ) => ({
    ...state,
    // isFetching: undefined,
    country: action.country,
  }),
  [FetchGeolocationError.type]: (
    state: CurrencyState,
    action: CurrencyAction
  ) => ({
    ...state,
    // isFetching: undefined,
    getGeolocationError: action.error,
  }),
});

export const fetchCurrencyEpic = (action$: ActionObservable) =>
  action$.pipe(
    ofType(
      LoginSuccess.type,
      GetSessionSuccess.type,
      FetchCurrency.type,
      SetCurrencySuccess.type
    ),
    switchMap(() =>
      getCurrencyApi().pipe(
        map((currency: Currency) => new FetchCurrencySuccess({currency})),
        buildCatchError(FetchCurrencyError)
      )
    )
  );

export const setCurrencyEpic = (action$: ActionObservable) =>
  action$.pipe(
    ofType(SetCurrency.type),
    switchMap(({currencyId}: CurrencyAction) =>
      setCurrencyApi({currencyId}).pipe(
        map(() => new SetCurrencySuccess()),
        buildCatchError(SetCurrencyError)
      )
    )
  );

export const fetchCurrenciesEpic = (action$: ActionObservable) =>
  action$.pipe(
    ofType(LoginSuccess.type, GetSessionSuccess.type),
    switchMap(() =>
      getCurrenciesApi().pipe(
        map(
          (currencies: Currency[]) => new FetchCurrenciesSuccess({currencies})
        ),
        catchError((error: ApiError) => {
          const m =
            error && error.error && error.error.message
              ? error.error.message
              : 'Unexpected server error';
          if (window.location.pathname.startsWith('/admin/on-boarding')) {
            captureMessage(
              `error fetching currency list on on-boarding - ${
                error.status ? error.status : '500'
              } - ${error.error ? JSON.stringify(error.error) : m}`
            );
          }
          return of(
            new FetchCurrenciesError({
              error: m,
            })
          );
        })
        // buildCatchError(FetchCurrenciesError)
      )
    )
  );

export const fetchGeolocationEpic = (action$: ActionObservable) =>
  action$.pipe(
    ofType(LoginSuccess.type, GetSessionSuccess.type),
    switchMap(() =>
      getGeolocationApi().pipe(
        map(
          response =>
            new FetchGeolocationSuccess({
              country: {
                code: response.country,
                type: 'country',
              },
            })
        ),
        buildCatchError(FetchGeolocationError)
      )
    )
  );
